/**
 * Dice facts in words and numbers, shared by the ★ legend's dice chips, the
 * map's matchup tags and the attack popup (design: tmp/rules-dice-chips-design
 * option B + X1/X2/X5/X6). Pure: no React.
 *
 * Normal combat is a d6 against a d6, and a tie goes to the defender.
 */
export const NORMAL_SIDES = 6

/** The chance one attacking die beats one defending die (a tie goes to the defender). */
export function dieBeatChance(attack: number, defend: number): number {
  let wins = 0
  for (let roll = 1; roll <= attack; roll++) wins += Math.min(roll - 1, defend)
  return wins / (attack * defend)
}

export const NORMAL_BEAT_CHANCE = dieBeatChance(NORMAL_SIDES, NORMAL_SIDES)

/** Who the dice favour against normal d6 v d6: the attacker, the defender or neither. */
export type DiceEdge = 'attacker' | 'defender' | 'even'

export function diceEdge(attack = NORMAL_SIDES, defend = NORMAL_SIDES): DiceEdge {
  const p = dieBeatChance(attack, defend)
  if (p > NORMAL_BEAT_CHANCE + 1e-9) return 'attacker'
  if (p < NORMAL_BEAT_CHANCE - 1e-9) return 'defender'
  return 'even'
}

export const percent = (x: number) => `${Math.round(x * 100)}%`

/** "so it is easier to attack" and friends, from the edge. */
export function edgeWords(edge: DiceEdge): string {
  return edge === 'attacker' ? 'so it is easier to attack' : edge === 'defender' ? 'so it is harder to attack' : 'an even fight'
}

/** "Easier to take." and friends: the short verdict closing an explanation. */
export function edgeVerdict(edge: DiceEdge): string {
  return edge === 'attacker' ? 'Easier to take.' : edge === 'defender' ? 'Harder to take.' : 'An even fight.'
}

/** The odds line of an expanded dice chip. */
export function oddsSentence(attack = NORMAL_SIDES, defend = NORMAL_SIDES): string {
  return `Each attacking die wins ${percent(dieBeatChance(attack, defend))} of the time (normally ${percent(NORMAL_BEAT_CHANCE)}).`
}

/** "the attacker rolls 5-sided dice and the defender 7-sided dice". */
export function dicePairWords(attack: number, defend: number): string {
  return `the attacker rolls ${attack}-sided dice and the defender ${defend}-sided dice`
}
